// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import React, {
  useRef, useState,
  useEffect, useMemo
} from 'react';

import EmptyList from './empty';
import Utils from '../../common/utils';
import Topbar from '../tabView/topbar';
import { TEXTS } from '../../constants';
import Checkbox from '../../atoms/checkbox';
import HeroCard from '../../atoms/card/hero';
import Dropdown from '../../atoms/dropdown/list';
import RefreshBtn from '../../atoms/button/refresh';
import { IScrollableContainer } from '../../common/types';
import TokenTransitButton from '../../atoms/button/tokenTransit';
import ScrollableContainer from '../../atoms/container/scrollable';
import { selectUserHeroes } from '../../repositories/store/slices';
import { fetchHeroes, updateHeroesChain } from '../../repositories/store/thunks';

// interfaces / types / consts
type Filter = 'active' | 'reserve' | 'inTransit';

// exports
export default function HeroCollectionList(): JSX.Element {
  // hooks configuration
  const dispatch = useDispatch();
  const { state } = useLocation();
  const containerRef = useRef<IScrollableContainer>(null);
  const [selectedTokens, setSelectedTokens] = useState<number[]>([]);
  const { nfts: heroNFTs, fetchStatus } = useSelector(selectUserHeroes);
  const [filter, setFilter] = useState<Filter>(state?.defaultTab || 'active');
  const [sortOrder, setSortOrder] = useState<'id' | 'name' | 'species'>('id');

  useEffect(() => { if (fetchStatus === 'idle') { dispatch(fetchHeroes() as unknown as AnyAction); } });

  // private methods
  const filteredNFTs = useMemo(() => {
    const filtered = heroNFTs.filter((nft) => {
      if ((filter === 'active' && nft.chain === 'L2')
        || (filter === 'reserve' && nft.chain === 'L1')
        || (filter === 'inTransit' && nft.chain === 'Transit')) { return true; }
      return false;
    });

    if (sortOrder === 'name') {
      filtered.sort((a, b) => (a.name! < b.name! ? -1 : 0));
    } else if (sortOrder === 'species') {
      filtered.sort((a, b) => (Utils.hero.getSpecies(a.traitIds) < Utils.hero.getSpecies(b.traitIds) ? -1 : 0));
    } else {
      filtered.sort((a, b) => a.tokenId - b.tokenId);
    }

    const locked = filtered.filter((nft) => nft.locked);
    const unLocked = filtered.filter((nft) => !nft.locked);
    return unLocked.concat(locked);
  }, [heroNFTs, sortOrder, filter]);
  const allSelected = filteredNFTs.filter((nft) => !nft.locked).length === selectedTokens.length;
  const deactivate = (): void => {
    dispatch(updateHeroesChain({ tokenIds: selectedTokens, targetChain: 'L1' }) as unknown as AnyAction);
    setSelectedTokens([]);
  };
  const activate = (): void => {
    dispatch(updateHeroesChain({ tokenIds: selectedTokens, targetChain: 'L2' }) as unknown as AnyAction);
    setSelectedTokens([]);
  };
  const getActivateBtn = (): JSX.Element => {
    if (filter === 'inTransit') { return <div />; }
    const type = filter === 'active' ? 'deactivate' : 'activate';
    return (
      <TokenTransitButton
        type={type}
        disabled={selectedTokens.length === 0}
        text={`${type} ${selectedTokens.length}/${filteredNFTs.length}`}
        onClick={filter === 'active' ? deactivate : activate}
      />
    );
  };
  const handleTabClick = (pFilter: Filter): void => {
    setFilter(pFilter);
    setSelectedTokens([]);
    containerRef.current?.resetScroll();
  };

  let emptyText = '';
  if (fetchStatus === 'loading') {
    emptyText = TEXTS.LIST_LOADING;
  } else if (fetchStatus === 'error') {
    emptyText = TEXTS.LIST_FAILED_TO_FETCH;
  } else if (!filteredNFTs.length) {
    if (filter === 'active') {
      emptyText = TEXTS.LIST_HERO_ACTIVE_EMPTY;
    } else if (filter === 'inTransit') {
      emptyText = TEXTS.LIST_HERO_TRANSIS_EMPTY;
    }
  }

  // render
  return (
    <div className="hero-collection-container">
      <Topbar
        defaultSelected={filter}
        tabs={[
          { key: 'active', display: 'Active Heroes', onClick: () => handleTabClick('active') },
          // { key: 'reserve', display: 'Hero Reserves', onClick: () => handleTabClick('reserve') },
          { key: 'in-transit', display: 'In-Transit', onClick: () => handleTabClick('inTransit') }]}
      />
      <ScrollableContainer
        ref={containerRef}
        fixed={(
          <div className="fixed-container">
            <div className="fixed">
              <Dropdown
                items={[
                  { key: 'ID', onClick: () => { setSortOrder('id'); } },
                  { key: 'Name', onClick: () => { setSortOrder('name'); } },
                  { key: 'Species', onClick: () => { setSortOrder('species'); } }
                ]}
              />
              <RefreshBtn
                isLoading={fetchStatus === 'loading'}
                onClick={() => {
                  setSelectedTokens([]);
                  dispatch(fetchHeroes() as unknown as AnyAction);
                }}
              />
              {filter !== 'inTransit' && (
                <div className="select-all">
                  <Checkbox
                    label="Select all"
                    checked={filteredNFTs.length ? allSelected : false}
                    onChanged={() => setSelectedTokens(allSelected ? [] : filteredNFTs.filter((nft) => !nft.locked).map((nft) => nft.tokenId))}
                  />
                </div>
              )}
              {getActivateBtn()}
            </div>
          </div>
        )}
      >
        {!filteredNFTs.length ? <EmptyList text={emptyText} linkType="opensea" />
          : (
            <div className="list">
              {filteredNFTs.map((nft) => (
                <HeroCard
                  nft={nft}
                  type="collection"
                  key={nft.tokenId}
                  disabled={nft.locked}
                  selected={selectedTokens.includes(nft.tokenId)}
                  onClicked={() => {
                    if (selectedTokens.includes(nft.tokenId)) {
                      setSelectedTokens((tokens) => tokens.filter((token) => token !== nft.tokenId));
                    } else {
                      setSelectedTokens((tokens) => [...tokens, nft.tokenId]);
                    }
                  }}
                />
              ))}
            </div>
          )}
      </ScrollableContainer>
    </div>
  );
}
